import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import {
  Plus,
  Users,
  Search,
  Edit,
  Trash2,
  Phone,
  UserCheck,
  Filter,
  ChevronRight,
  ChevronLeft,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import ParentForm from './ParentForm';
import { createParent, getParents } from '../../api/services/parentApi';
import SettingsHeader from '../../ui/SettingsHeader';

const Parents = () => {
  const { user } = useAuth();
  const {
    students: apiStudents,
    loadParents: loadParentsContext, // Keep context reload for global consistency
  } = useData();
  
  // Local state for server-side pagination
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 1
  });

  const [showForm, setShowForm] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterRelation, setFilterRelation] = useState('all');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [parentToDelete, setParentToDelete] = useState(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchParents = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getParents(pagination.page, pagination.limit, debouncedSearch);
      if (response.success) {
        setParents(response.data || []);
        if (response.pagination) {
           setPagination(prev => ({ ...prev, ...response.pagination }));
        }
      } else {
        setParents([]);
      }
    } catch (error) {
      console.error("Failed to fetch parents", error);
      toast.error("Failed to load parents");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, debouncedSearch]);

  useEffect(() => {
    fetchParents();
  }, [fetchParents]);

  // Enhance parents with student information
  // Note: Since we are paginating, we might miss some student details if not populated by backend.
  // Ideally backend should return populated students. Assuming it does or we map locally if IDs are present.
  const parentsWithStudents = React.useMemo(() => {
    return parents.map(parent => {
      const linkedStudents = parent.studentIds || [];
      return {
        ...parent,
        studentCount: linkedStudents.length,
        students: linkedStudents.map(student => {
          if (typeof student === 'object') {
            return {
              id: student._id || student.id,
              name: student.firstName ? `${student.firstName} ${student.lastName}` : 'Unnamed',
              registrationId: student.studentRegistrationId,
              status: student.status || 'active'
            };
          }
          // Fallback if only ID is provided (though normally backend populates)
          return { id: student, name: 'Linked', registrationId: '', status: 'active' };
        })
      };
    });
  }, [parents]);

  // Client-side relation filter (since backend might not support it yet, or we assume backend handles search)
  // If backend supports filtering by relation, we should pass it. For now keeping client side filter on the current page data 
  // OR strictly filtered results. Ideally backend should handle this.
  // For now, let's filter the displayed page to avoid complexity if API doesn't support it, 
  // BUT this is bad UX for pagination. Assuming 'all' for now or simple client filter on page.
  const displayedParents = React.useMemo(() => {
     if (filterRelation === 'all') return parentsWithStudents;
     return parentsWithStudents.filter(p => p.relation === filterRelation);
  }, [parentsWithStudents, filterRelation]);

  const handleCreateOrUpdate = async (parentData) => {
    setIsSubmitting(true);
    try {
      const { createParent, updateParent } = await import('../../api/services/parentApi');
      const isEdit = !!selectedParent;
      
      const response = isEdit 
          ? await updateParent(selectedParent._id, parentData)
          : await createParent(parentData);

      if (response.success) {
          toast.success(response.message || `Parent ${isEdit ? 'updated' : 'created'} successfully!`);
          setShowForm(false);
          setSelectedParent(null);
          fetchParents();
          loadParentsContext(); // Refresh global context in background
      } else {
          toast.error(response.message || 'Operation failed');
      }
    } catch (error) {
        toast.error(error.message || 'Something went wrong');
    } finally {
        setIsSubmitting(false);
    }
  };

  const handleDeleteParent = async (parentId) => {
    setParentToDelete(parentId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!parentToDelete) return;
    
    setIsSubmitting(true);
    try {
      const { deleteParent } = await import('../../api/services/parentApi');
      const response = await deleteParent(parentToDelete);
      if (response.success) {
        toast.success('Parent deleted successfully');
        fetchParents();
        loadParentsContext();
        setShowDeleteModal(false);
        setParentToDelete(null);
      } else {
        toast.error(response.message || 'Delete failed');
      }
    } catch (error) {
      toast.error(error.message || 'Delete failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const relations = ['all', 'Father', 'Mother', 'Guardian', 'Other'];

  return (
    <div className="space-y-6">
      <SettingsHeader
        title="Parent Directory"
        showBack={false}
        icon={Users}
        buttons={[
          {
            label: "Add Parent",
            icon: Plus,
            onClick: () => { setSelectedParent(null); setShowForm(true); },
            variant: "default",
          },
        ]}
      >
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative min-w-[180px]">
            <Filter className="absolute w-3.5 h-3.5 text-gray-400 transform -translate-y-1/2 left-2.5 top-1/2 pointer-events-none" />
            <select
              value={filterRelation}
              onChange={e => setFilterRelation(e.target.value)}
              className="w-full pl-8 pr-7 py-1.5 text-xs bg-gray-50/50 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500/20 focus:border-primary-500 transition-all appearance-none cursor-pointer hover:bg-white"
            >
              {relations.map(r => (
                <option key={r} value={r}>{r === 'all' ? 'All Relations' : r}</option>
              ))}
            </select>
            <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
              <ChevronRight className="w-3.5 h-3.5 rotate-90" />
            </div>
          </div>

          <div className="relative w-full sm:w-64">
            <Search className="absolute w-3.5 h-3.5 text-gray-400 transform -translate-y-1/2 left-2.5 top-1/2" />
            <input
              type="text"
              placeholder="Search parents..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full py-1.5 pl-8 pr-3 text-xs bg-gray-50/50 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500/20 focus:border-primary-500 transition-all hover:bg-white"
            />
          </div>
        </div>
      </SettingsHeader>

      {/* Parents List Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
             <div className="p-12 text-center text-gray-500 flex flex-col items-center">
                 <Loader2 className="w-8 h-8 animate-spin text-[var(--primary-color)] mb-2" />
                 <p className="text-sm">Loading directory...</p>
             </div>
        ) : displayedParents.length === 0 ? (
           <div className="p-12 text-center text-gray-500">
               <Users className="w-12 h-12 mx-auto text-gray-300 mb-3" />
               <p>No parents found matching your search.</p>
           </div>
        ) : (
        <>
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50">
                <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Name/Relation</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Linked Students</th>
                    <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                {displayedParents.map((parent) => (
                    <tr key={parent._id} className="hover:bg-gray-50 transition-colors group">
                    <td className="px-6 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                        <div 
                            className="h-8 w-8 rounded-full flex items-center justify-center font-bold text-xs mr-3"
                            style={{ 
                            backgroundColor: 'color-mix(in srgb, var(--primary-color), white 90%)',
                            color: 'var(--primary-color)'
                            }}
                        >
                            {parent.name?.charAt(0)}
                        </div>
                        <div>
                            <div className="text-xs font-bold text-gray-900">{parent.name}</div>
                            <div className="text-[10px] text-gray-400 bg-gray-50 border border-gray-100 px-1.5 py-0.5 rounded-md font-bold uppercase tracking-tighter mt-0.5">{parent.relation}</div>
                        </div>
                        </div>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap">
                        <div className="flex flex-col gap-0.5">
                            <span className="text-xs text-gray-700 font-bold">{parent.phoneNumber}</span>
                            {parent.email && (
                                <span className="text-[10px] text-gray-500 font-medium lowercase">
                                    {parent.email}
                                </span>
                            )}
                            {parent.hasWhatsapp && (
                                <span 
                                    className="text-[10px] flex items-center gap-1 font-bold"
                                    style={{ color: 'var(--primary-color)' }}
                                >
                                    <UserCheck className="w-3 h-3" /> WhatsApp
                                </span>
                            )}
                        </div>
                    </td>
                    <td className="px-6 py-3">
                        <div className="flex flex-wrap gap-1">
                            {parent.students.length > 0 ? (
                                parent.students.map(s => (
                                    <span 
                                    key={s.id} 
                                    className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border truncate max-w-[150px]"
                                    style={{ 
                                        backgroundColor: 'color-mix(in srgb, var(--primary-color), white 95%)',
                                        color: 'var(--primary-color)',
                                        borderColor: 'color-mix(in srgb, var(--primary-color), white 80%)'
                                    }}
                                    >
                                        {s.name} {s.registrationId && `(${s.registrationId})`}
                                    </span>
                                ))
                            ) : (
                                <span className="text-xs text-gray-400 italic">No students linked</span>
                            )}
                        </div>
                    </td>
                    <td className="px-6 py-3 text-right whitespace-nowrap">
                        <div className="flex justify-end gap-2">
                            <button 
                                onClick={() => { setSelectedParent(parent); setShowForm(true); }}
                                className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-gray-100 rounded transition-colors"
                            >
                                <Edit className="w-3.5 h-3.5" />
                            </button>
                            <button 
                                onClick={() => handleDeleteParent(parent._id)}
                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </td>
                    </tr>
                ))}
                </tbody>
            </table>
            </div>

            {/* Pagination Controls */}
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between p-4 bg-gray-50 border-t">
                <div className="text-xs font-medium text-gray-500">
                    Showing <span className="text-gray-900">{parents.length}</span> of <span className="text-gray-900">{pagination.total}</span> parents
                </div>
                <div className="flex items-center space-x-3">
                    <select 
                    value={pagination.limit} 
                    onChange={(e) => setPagination(p => ({ ...p, limit: Number(e.target.value), page: 1 }))}
                    className="p-1.5 text-xs font-bold text-gray-700 bg-white border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-gray-300"
                    >
                    <option value={10}>10 / page</option>
                    <option value={20}>20 / page</option>
                    <option value={50}>50 / page</option>
                    <option value={100}>100 / page</option>
                    </select>
                    <div className="flex items-center bg-white border border-gray-200 rounded-lg p-1 shadow-sm">
                        <button
                        disabled={pagination.page === 1}
                        onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                        className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-md disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                        >
                        <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="px-3 text-xs font-bold text-gray-700 border-x border-gray-100">
                        {pagination.page} / {pagination.pages || 1}
                        </span>
                        <button
                        disabled={pagination.page >= pagination.pages}
                        onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                        className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-md disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                        >
                        <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        </>
        )}
      </div>

      {/* Parent Form Modal */}
      {showForm && (
        <ParentForm
          parent={selectedParent}
          onClose={() => {
            setShowForm(false);
            setSelectedParent(null);
          }}
          onSubmit={handleCreateOrUpdate}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in duration-300">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Confirm Delete</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Are you sure you want to delete this parent? This action will unlink them from all students and cannot be undone.
              </p>
            </div>
            <div className="flex items-center gap-3 p-4 bg-gray-50 mt-2">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => {
                  setShowDeleteModal(false);
                  setParentToDelete(null);
                }}
                className="flex-1 px-4 py-2.5 text-sm font-bold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={confirmDelete}
                className="flex-1 px-4 py-2.5 text-sm font-bold text-white bg-red-600 rounded-xl hover:bg-red-700 shadow-lg shadow-red-200 transition-all flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete Parent'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Parents;
